import { useEffect, useState } from 'react'
import { Form, Modal, Row, Button } from 'react-bootstrap'
import api from '../../../config/AxiosInstance.js'
import { convertDateWithoutTime } from '../../../helpers/constants/constant.js'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common.js'
import Label from '../../Label/Input/index.jsx'
import { Download, RefreshCw } from 'lucide-react'
import Swal from 'sweetalert2'

function ModalDetailBatch(props) {
  const showModal = props.show
  const [data, setData] = useState({})
  const [loading, setLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownloadCoa = async (id) => {
    try {
      setIsDownloading(true)
      const response = await api.get(`master/batch/${id}/download-coa`, {
        responseType: 'arraybuffer'
      })

      const contentType = response.headers['content-type']
      if (contentType && contentType.includes('application/json')) {
        const text = new TextDecoder().decode(response.data)
        const json = JSON.parse(text)
        throw new Error(json.errors?.message || 'An error occurred')
      }

      const { data } = response
      const blob = new Blob([data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `coa-batch-${id}.pdf`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      let errMsg = 'An error occurred'
      if (err.response && err.response.data instanceof ArrayBuffer) {
        try {
          const text = new TextDecoder().decode(err.response.data)
          const json = JSON.parse(text)
          errMsg = json.errors?.message || json.message || errMsg
        } catch (e) {
          // ignore parsing error
        }
      } else {
        errMsg = err?.response?.data?.errors?.message || err.message || errMsg
      }

      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsDownloading(false)
    }
  }

  useEffect(
    function () {
      if (showModal) {
        fetchBatch(props.data)
      }
    },
    [props.data, showModal]
  )

  async function fetchBatch(id) {
    try {
      setIsFetching(true)
      const res = await api.get(`master/batch/${id}`)

      const result = res.data

      setData({
        ...result.data,
        date_patch: result.data.date_patch
          ? new Date(result.data.date_patch).toISOString().split('T')[0]
          : null,
        exp_date: result.data.exp_date
          ? new Date(result.data.exp_date).toISOString().split('T')[0]
          : null,
        manufacturing_date: result.data.manufacturing_date
          ? new Date(result.data.manufacturing_date).toISOString().split('T')[0]
          : null,
        coa: result.data.coa
      })
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'

      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsFetching(false)
    }
  }

  const handleSync = async () => {
    try {
      setLoading(true)
      const res = await api.get(`master/batch/${props.data}/sync`)

      if (res.status === 200) {
        Swal.fire({
          title: 'Success!',
          text: 'Synced successfully',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#ff9f43'
        }).then((result) => {
          if (result.isConfirmed) {
            props.onHide()
          }
        })
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal {...props} size="md" centered>
      <Form className="d-grid gap-2">
        <Modal.Header closeButton>
          <Modal.Title>Detail Batch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isFetching ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Row>
              <Label label="item" value={data.item?.item_name} col={12} />
              <Label label="batch" value={data.batch} col={6} />
              <Label
                label="manufacturing date"
                value={convertDateWithoutTime(data.manufacturing_date)}
                col={6}
              />
              <Label
                label="exp. date"
                value={
                  data.exp_date ? convertDateWithoutTime(data.exp_date) : '-'
                }
                col={6}
              />
              <Label
                label="date. patch"
                value={
                  data.date_patch
                    ? convertDateWithoutTime(data.date_patch)
                    : '-'
                }
                col={6}
              />
              <Label
                label="note"
                value={data.note ? data.note : '-'}
                col={12}
              />
              <div className="col-md-12 col-12 mb-3">
                <Form.Group>
                  <Form.Label
                    className="text-uppercase fw-bold text-muted"
                    style={{ fontSize: '13px', letterSpacing: '0.5px' }}
                  >
                    COA Document
                  </Form.Label>
                  {data.coa ? (
                    <button
                      type="button"
                      onClick={() => handleDownloadCoa(props.data)}
                      disabled={isDownloading}
                      className="btn btn-outline-secondary d-flex align-items-center justify-content-between p-3 rounded-3 w-100 bg-white"
                      style={{
                        border: '1px solid #e9ecef',
                        transition: 'all 0.2s',
                        marginTop: '0.5rem'
                      }}
                    >
                      <div className="d-flex align-items-center text-truncate">
                        <div
                          className="d-flex align-items-center justify-content-center rounded bg-light me-3"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <Download className="text-primary" size={16} />
                        </div>
                        <div className="text-start text-truncate">
                          <div
                            className="fw-bold text-dark text-truncate"
                            style={{ fontSize: '14px', maxWidth: '250px' }}
                          >
                            {data.coa}
                          </div>
                          <small
                            className="text-muted"
                            style={{ fontSize: '11px' }}
                          >
                            Click to download
                          </small>
                        </div>
                      </div>
                      <div>
                        {isDownloading ? (
                          <div
                            className="spinner-border text-primary"
                            role="status"
                            style={{
                              width: '16px',
                              height: '16px',
                              borderWidth: '2px'
                            }}
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          <Download className="text-muted" size={16} />
                        )}
                      </div>
                    </button>
                  ) : (
                    <div className="text-muted py-2">
                      No COA document available
                    </div>
                  )}
                </Form.Group>
              </div>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex w-100 justify-content-end gap-2">
            <Button
              variant="primary"
              className="text-white d-flex align-items-center gap-2"
              onClick={handleSync}
              disabled={loading}
              style={{ backgroundColor: '#203568', borderColor: '#203568' }}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Syncing...' : 'Sync to ERP'}
            </Button>
            <Button
              variant="secondary"
              className="text-white"
              onClick={props.onHide}
              style={{ backgroundColor: '#ff9f43', borderColor: '#ff9f43' }}
            >
              Ok
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default ModalDetailBatch
